import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { getPaymentStatus } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PLAN_NAME = { standard: "Standard", pro: "Pro", pro_max: "Pro Max" };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        setPlan(res.data.plan);
        if (res.data.payment_status === "paid") { setState("paid"); refreshUser(); return; }
        if (["failed", "expired", "refunded"].includes(res.data.payment_status)) { setState("failed"); return; }
      } catch (e) {
        console.warn("payment status poll failed:", e?.response?.status || e?.message);
      }
      if (attempts.current >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  return (
    <div className="grid min-h-screen place-items-center bg-background px-6 text-foreground" data-testid="payment-success-page">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center"><Logo /></div>
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-6 text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This usually takes a couple of seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/15 text-emerald-500">
              <CheckCircle2 className="h-9 w-9" />
            </div>
            <h1 className="mt-6 text-3xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="payment-success-title">
              You're on {PLAN_NAME[plan] || "your new plan"}.
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">Payment received. All models on your tier are unlocked now.</p>
            <Button asChild className="mt-8 h-12 rounded-xl px-6" data-testid="payment-success-chat-btn">
              <Link to="/chat">Start chatting <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-destructive/15 text-destructive">
              <XCircle className="h-9 w-9" />
            </div>
            <h1 className="mt-6 text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state === "timeout"
                ? "Your bank is taking a moment. Your plan will activate automatically once Stripe confirms — check back in a minute."
                : "No charge was made. You can try again any time."}
            </p>
            <Button asChild variant="outline" className="mt-8 h-12 rounded-xl px-6" data-testid="payment-back-btn">
              <Link to="/upgrade">Back to plans</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
